import React, { useCallback, useRef, useState } from 'react';
import { getLabel } from '../../../data/labels';
import MyBetsShareCard from '../MyBetsShareCard';
import { toPng } from 'html-to-image';
import { getConfiguration } from '../../utils/configuration';
import { systemCode, xs_isMobile } from '../../..';
import SocialShareButtons from './SocialShareButtons';
import { getShareCardConfig } from '../MyBetsShareCardConfig';

//   Gestisce tutta la logica di download/condivisione del ticket: stato download, setting, html-to-img e condivisione web share api/android/diretto
function ShareCardDownloader(props) {
    const conf = getConfiguration();
    const ticketId = props.ticket?.id || props.ticketId;
    const ticketUrl = props.ticketUrl;
    const refShareCard = useRef(null);
    const [downloadStatus, setDownloadStatus] = useState('idle');
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [hideAmounts, setHideAmounts] = useState(false);
    const [showShareCard, setShowShareCard] = useState(false);
    const [copyLinkText, setCopyLinkText] = useState(null);
    const isDebug = false;

    const dataUrlToBlob = async (dataUrl) => {
        const res = await fetch(dataUrl);
        return await res.blob();
    };

    const triggerErrorFeedback = () => {
        setDownloadStatus('error');
        setShowShareCard(false);
        setTimeout(() => setDownloadStatus('idle'), 3000);
    };


    const handleDownloadImage = useCallback((e) => {
        if (e) e.stopPropagation();
        if (!ticketId) return;

        setDownloadStatus('loading');
        setShowShareCard(true);

        setTimeout(() => {
            if (refShareCard.current === null) {
                triggerErrorFeedback();
                return;
            }

            const width = 600;
            const height = refShareCard.current.scrollHeight;
            const filter = (node) => {
                if (node.tagName === 'IFRAME') return false;
                if (node.tagName === 'IMG' && node.naturalWidth === 0) return false;
                return true;
            };

            toPng(refShareCard.current, {
                cacheBust: true,
                skipFonts: true,
                filter,
                backgroundColor: getShareCardConfig().backgroundColor,
                pixelRatio: 2,
                useCORS: true,
                allowTaint: true,
                width,
                height,
                style: {
                    transform: 'scale(1)',
                    transformOrigin: 'top left',
                    width: `${width}px`,
                    height: `${height}px`,
                    maxHeight: 'none',
                },
            })
                .then(async (dataUrl) => {
                    const android = window.android;
                    const fileName = `Ticket_${ticketId}.png`;
                    const fileType = 'image/png';

                    // Android
                    if (android && typeof android.shareFiles === 'function') {
                        try {
                            const pureBase64 = dataUrl.split(',')[1];
                            android.shareFiles(JSON.stringify({
                                title: `${conf.sys} | Condivisione Ticket`,
                                text: getLabel('betslip.reservation.share_bet'),
                                url: ticketUrl || '',
                                files: [{ name: fileName, type: fileType, dataUrl: pureBase64 }],
                            }));
                            setDownloadStatus('idle');
                            setShowShareCard(false);
                            return;
                        } catch {
                            triggerErrorFeedback();
                            return;
                        }
                    }

                    // webshare
                    if (navigator.canShare && xs_isMobile && !android) {
                        try {
                            const blob = await dataUrlToBlob(dataUrl);
                            const file = new File([blob], fileName, { type: fileType });

                            if (navigator.canShare({ files: [file] })) {
                                await navigator.share({
                                    files: [file],
                                    title: `${conf.sys} | Ticket`,
                                    text: getLabel('betslip.reservation.share_bet'),
                                });
                                setDownloadStatus('idle');
                                setShowShareCard(false);
                                return;
                            }
                        } catch (err) {
                            if (err.name === 'AbortError') {
                                setDownloadStatus('idle');
                                setShowShareCard(false);
                            } else {
                                const link = document.createElement('a');
                                link.download = fileName;
                                link.href = dataUrl;
                                link.click();
                                setDownloadStatus('idle');
                                setShowShareCard(false);
                            }
                            return;
                        }
                    }

                    // desktop
                    if (!xs_isMobile || (!navigator.canShare && !android)) {
                        const link = document.createElement('a');
                        link.download = fileName;
                        link.href = dataUrl;
                        link.click();
                        setDownloadStatus('idle');
                        setShowShareCard(false);
                    } else {
                        triggerErrorFeedback();
                    }
                })
                .catch(() => triggerErrorFeedback());

        }, 800);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketId, conf.sys, ticketUrl, hideAmounts]);

    function handleCopyLink() {
        if (systemCode === 'DOMUSBET' && xs_isMobile && navigator.share) {
            navigator.share({
                url: ticketUrl,
                title: `${conf.sys} | ${getLabel('betslip.reservation.share')}`,
                text: getLabel('betslip.reservation.share_bet'),
            }).catch(() => { });
        } else {
            const android = window.android;
            if (android && typeof android.writeTextClipboard === 'function') {
                android.writeTextClipboard(ticketUrl);
                triggerCopyFeedback();
            } else {
                navigator.clipboard.writeText(ticketUrl).then(triggerCopyFeedback);
            }
        }
    }

    function triggerCopyFeedback() {
        setCopyLinkText(getLabel('checkbet.link_copied'));
        setTimeout(() => setCopyLinkText(null), 1000);
    }


    return (
        <>
            <SocialShareButtons
                ticketId={ticketId}
                copyLinkText={copyLinkText}
                onCopyLink={handleCopyLink}
                downloadStatus={downloadStatus}
                onDownloadClick={() => setShowSettingsModal(true)}
            />

            {conf.sdt && (
                <>
                    {/* Modal impostazioni */}
                    {showSettingsModal && (
                        <div className="share-modal__overlay" onClick={() => setShowSettingsModal(false)}>
                            <div className="share-modal__content" onClick={(e) => e.stopPropagation()} >
                                <h4 className="share-modal__title">
                                    {getLabel('general.ticket', 'Ticket')}
                                </h4>

                                <label className="share-modal__label">
                                    <input
                                        type="checkbox"
                                        className="share-modal__checkbox"
                                        checked={hideAmounts}
                                        onChange={(e) => setHideAmounts(e.target.checked)}
                                    />
                                    {getLabel('mybets.hide.stake_and_win_pot', 'Nascondi importo e vincita potenziale')}
                                </label>

                                <div className="share-modal__actions">
                                    <button className="share-modal__btn share-modal__btn--cancel" onClick={() => setShowSettingsModal(false)}>
                                        {getLabel('book_bet_popup.dismiss_btn', 'Annulla')}
                                    </button>
                                    <button
                                        className="share-modal__btn share-modal__btn--proceed"
                                        onClick={(e) => {
                                            setShowSettingsModal(false);
                                            handleDownloadImage(e);
                                        }}
                                    >
                                        {getLabel('general.next')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {showShareCard && (
                        <div className={isDebug ? 'share-capture-wrapper--debug' : 'share-capture-wrapper--hidden'}>
                            {ticketId ? (
                                <MyBetsShareCard
                                    ref={refShareCard}
                                    ticketId={ticketId}
                                    isSnapshot={true}
                                    winPot={props.winPot}
                                    totalOdd={props.totalOdd}
                                    totalAmount={props.totalAmount}
                                    dateTimestamp={props.dateTimestamp}
                                    status={props.status}
                                    hideAmounts={hideAmounts}
                                />
                            ) : (
                                <div className="share-capture__error">
                                    {getLabel('control_panel.print_tickets.search_results.not_ticket_found')}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </>
    );
}

export default ShareCardDownloader;